import { create } from "zustand";
import { apiAuthFetch } from "@/lib/authFetch";

// Same pattern as threadHistoryProvider: skip network calls for short base62 client IDs
// that assistant-ui generates before the server-side GUID is resolved. The backend
// would 404 on them anyway, and the donut keeps showing the prior value.
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Wire-format snapshot returned by the backend on /threads/{id}/messages,
 * /threads/{id}/context-budget, and SSE FinishEvent.
 *
 * Tokens is a long on the backend; carried as `number` here. The provider's
 * ContextTokenBudget caps out at ~10^6, so it stays well within Number.MAX_SAFE_INTEGER.
 */
export type ContextWindowSnapshot = {
  tokens: number;
  budget: number;
  percent: number;
  trimmed: boolean;
  // Per-thread flag — true when the thread is on the extended ceiling and skipping compaction.
  extendedContextEnabled: boolean;
  // True once the user has been shown (and dismissed) the compaction-vs-extend modal at
  // least once on this thread. Suppresses re-prompting on subsequent sends.
  compactionPromptAcknowledged: boolean;
  // True when the active provider has a non-zero ExtendedContextTokenBudget. Only then is
  // the extend option offered in the modal.
  extendedContextAvailable: boolean;
};

type ContextBudgetState = {
  byThread: Record<string, ContextWindowSnapshot>;

  /** Replace the snapshot for a thread. Pass null to clear (e.g. on thread switch). */
  set: (threadId: string, snapshot: ContextWindowSnapshot | null) => void;

  /** Fetch the snapshot for a specific leaf message id and stamp the store. */
  refresh: (threadId: string, leafMessageId: string) => Promise<void>;
};

export const useContextBudgetStore = create<ContextBudgetState>((set) => ({
  byThread: {},

  set(threadId, snapshot) {
    set((s) => {
      if (snapshot === null) {
        const { [threadId]: _drop, ...rest } = s.byThread;
        return { byThread: rest };
      }
      return { byThread: { ...s.byThread, [threadId]: snapshot } };
    });
  },

  async refresh(threadId, leafMessageId) {
    if (!GUID_PATTERN.test(leafMessageId)) return;
    try {
      const res = await apiAuthFetch(
        `api/threads/${encodeURIComponent(threadId)}/context-budget?leafMessageId=${encodeURIComponent(leafMessageId)}`,
      );
      if (!res.ok) {
        // 404 = leaf isn't on this thread (likely a stale leaf id from an in-flight
        // branch switch). 503 = no resolved provider. Either way silently degrade —
        // the donut keeps showing the prior value.
        return;
      }
      const snap = (await res.json()) as ContextWindowSnapshot;
      set((s) => ({ byThread: { ...s.byThread, [threadId]: snap } }));
    } catch {
      // Network blip — keep showing the last known value.
    }
  },
}));
